import { useState, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { PaginatorPageChangeEvent } from 'primereact/paginator';

import { useExtractServerError } from 'hooks';

import { useSubmitListZeroMutation } from './mutations';
import { useRecentSubmissionsQuery } from './queries';
import { Employee, ListSubmitResponse } from './ListZero.types';
import { toSubmissionRows } from './toSubmissionRows';
import ListZeroUi from './ListZero.ui';

export default function ListZero() {
  const toastRef = useRef<Toast>(null);
  const { extractErrorMessage } = useExtractServerError();

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [fileName, setFileName] = useState('');
  const [response, setResponse] = useState<ListSubmitResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [pagination, setPagination] = useState({ page: 1, first: 0, rows: 20 });

  const submitMutation = useSubmitListZeroMutation();
  const recentSubmissions = useRecentSubmissionsQuery({
    page: pagination.page,
    rows: pagination.rows,
  });

  function handlePageChange(event: PaginatorPageChangeEvent) {
    setPagination({ page: event.page + 1, first: event.first, rows: event.rows });
  }

  function parseCSV(text: string): Employee[] {
    const lines = text.split('\n').filter((line) => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));

    return lines.slice(1).map((line) => {
      const values: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const employee: any = {};
      headers.forEach((header, index) => {
        const value = values[index] || '';
        employee[header] = value === 'null' ? null : value;
      });

      return employee as Employee;
    });
  }

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setResponse(null);
    setError(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const parsed = parseCSV(text);
      setEmployees(parsed);

      toastRef.current?.show({
        severity: 'info',
        summary: 'CSV Loaded',
        detail: `${parsed.length} employees parsed from ${file.name}`,
        life: 3000,
      });
    };
    reader.readAsText(file);

    // Reset input so same file can be re-uploaded
    e.target.value = '';
  }

  function handleSubmit() {
    if (employees.length === 0) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'No Data',
        detail: 'Please upload a CSV file first',
        life: 3000,
      });
      return;
    }

    setResponse(null);
    setError(null);

    submitMutation.mutate(
      { count: employees.length, employees },
      {
        onSuccess: (res) => {
          setResponse(res.data as any);
          setPagination({ page: 1, first: 0, rows: pagination.rows });
          recentSubmissions.refetch();
          toastRef.current?.show({
            severity: 'success',
            summary: 'Submitted',
            detail: `List0 submitted successfully - ${employees.length} employees`,
            life: 5000,
          });
        },
        onError: (err: any) => {
          const errorResponse = err?.response?.data;
          if (errorResponse) {
            setResponse(errorResponse);
            setError(null);
          } else {
            const errorMessage = extractErrorMessage(err);
            setError(errorMessage);
          }
          toastRef.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: errorResponse?.message || extractErrorMessage(err),
            life: 5000,
          });
        },
      }
    );
  }

  function handleDownloadSample() {
    const headers = [
      'NATIONALIDEXPIRYDATE','OCCUPATION','COMPANYREGISTRATIONNUMBER','PASSPORTEXPIRYDATE',
      'SerialNo','COUNTRY','COMPANYCODE','COMPANYNAME','BUCODE','BUNAME',
      'AZADEAID','PERSONID','EMPFIRSTNAME','EMPFATHERNAME','EMPFAMILYNAME',
      'LEAVEDATE','LEAVEREASON','RELATION','DOB','GENDER','NATIONALITY','MARITAL',
      'COSTSHARING','POSITION','CLASS','GRADE','MOBILEPHONE','EMAIL','NATIONALID',
      'IQAMANUMBER','IQAMAEXPIRYDATE','SPONSORID','KID','QID','EMIRATESID',
      'PASSPORTNUMBER','UIDNUMBER','VISA','VISAISSUANCE','MEMBERTYPE','ENTITYTYPE',
      'ESTABLISHMENTID','INSURED','RESIDENTIALLOCATION','WORKLOCATION','SALARYBRACKET',
      'HASCOMMISSION','USER_PERSON_TYPE','TIMESET',
    ];

    const sampleRows = [
      ['','','null','','12285','Jordan','7256','Azadea for Telecommunications and Technology L.L.C.','05CBEORT','Retail Services','321456','4108645','Osama','Ibrahim','AlHajaj','','','1','04/03/2001','M','Jordanian','Single','Co-Nil','Stock Management Services Administrator','ESSENTIAL - CLASS SP','5','+962790994072','Osama.Alhajaj@azadea.com','2000262019','','','','','','','','','','','','','xx','','','','','No','Employee','FULLT'],
      ['','','null','','11365','Bahrain','2382','Zali Trading CO WLL','04PBRR05','Pull&Bear Bahrain CC','311458','4064103','Hawra','Sayed Baqer Majeed Radhi','Ebrahim','01/03/2026','Resignation','1','16/08/1999','F','Bahraini','Single','Co-Nil','Sales Associate','ESSENTIAL - CLASS SP','4','+97336996122','','990804585','','','','','','','','','','','','','xx','','','','','Yes','National Employee','FULLT'],
      ['','','null','','11468','Bahrain','2379','Batco Trading CO WLL','04VGNR05','Virgin Bahrain CC New','312502','4069612','Qasim','Mahmood Ali Habib','Alhaider','','','1','14/10/2004','M','Bahraini','Single','Co-Nil','Sales Associate','ESSENTIAL - CLASS SP','4','+97333322436','Qasim.312502@azadea.com','041004710','','','','','','','','','','','','','xx','','','','','Yes','National Employee','PARTT'],
      ['','','null','','11295','Bahrain','2381','Zahnoor Trading CO WLL','04ZARL22','Zara Marassi Galleria','291289','3474877','Anamul','','Islam','','','1','06/09/1985','M','Bangladeshi','Married','Co-Nil','Tailor','ESSENTIAL - CLASS SP','2','+97336865800','Anamul.291289@azadea.com','BE0911349','','','','','','','','','','','','','xx','','','','','No','Expatriate Employee','FULLT'],
      ['','','null','','12112','Cyprus','4628','LUGOVAYA HOLDINGS LIMITED','21SGHL01','Sunglass Hut My Mall','320537','4106774','Paraskevi','','Georgiou','','','1','28/05/2003','F','Cypriot','Single','Co-Nil','Sales Associate','ESSENTIAL - CLASS SP','5','+35797803390','paraskevi.320537@azadea.com','','','','','','','','','','','','','','xx','','','','','Yes','National Employee','PARTT'],
      ['','','null','','12397','United Arab Emirates','13202','Mafa Consultancy DMCC Branch','01MAFBOL','Other Lifestyle Division','2828','4091412','Ralph','','Bastormaji','','','1','15/10/1982','M','Armenian','Married','Co-Nil','Retail Operations Lead','ESSENTIAL PLUS - CLASS A','9','','Ralph.Bastormagi@azadea.com','','','','','','','784-1982-2583691-9','AR0585983','784198225836919','201/2020/2399227','4 = Dubai','4 = Expat whos residency is issued in Dubai - File Number','3 = Establishment','xx','NO','110','110','Salary greater than 12,000 AED per month','No','Expatriate Employee','FULLT'],
      ['','','null','','11845','Bahrain','2380','Zadina Trading CO WLL','04ETYL22','Eataly Marassi Galleria','319505','4102858','Kimberlee','Capangpangan','Gozun','','','1','21/09/1986','M','Philippine','Single','Co-Nil','Commis','ESSENTIAL - CLASS SP','3','+97336996621','kimberlee.319505@azadea.com','860955621','','','','','','','','','','','','','xx','','','','','Yes','Expatriate Employee','FULLT'],
      ['','','null','','12150','Jordan','7256','Azadea for Telecommunications and Technology L.L.C.','05CBEOFI','Financial Services','282434','2668613','Majd','Ali','Khatatbeh','','','1','16/05/1990','F','Jordanian','Married','Co-Nil','Senior Assets Services Specialist','ESSENTIAL PLUS - CLASS A','8','+962795115716','Majd.Khatatbeh@azadea.com','9902050549','','','','','','','','','','','','','xx','','','','','No','Employee','FULLT'],
      ['','','null','','11085','Kuwait','2260','Group Seven for General Trading & Contracting Co.','03MDTR03','Massimo Dutti Marina Mall','319595','4103041','Riham','','Mohammed Mahmoud','','','1','27/09/1995','F','Lebanese','Single','Co-Nil','Sales Associate','ESSENTIAL - CLASS SP','5','+96555802304','riham.319595@azadea.com','295092700901','','','','295092700901','','','','','','','','','xx','','','','','Yes','Expatriate Employee','FULLT'],
      ['','','null','','12401','United Arab Emirates','13202','Mafa Consultancy DMCC Branch','01MAFBFD','Fashion Direction','25232','4082003','Mohamad','','Hassan Zbib','','','1','25/08/1973','M','Lebanese','Married','Co-Nil','Window Dressing Lead','ESSENTIAL PLUS - CLASS A','9','+971505518311','mohamad.zbib@azadea.com','','','','','','','784-1973-8254710-3','LR3002173','784197382547103','201/2024/7945185','4 = Dubai','4 = Expat whos residency is issued in Dubai - File Number','3 = Establishment','xx','NO','110','110','Salary greater than 12,000 AED per month','No','Expatriate Employee','FULLT'],
      ['','','null','','12177','Jordan','7256','Azadea for Telecommunications and Technology L.L.C.','05CBEOHR','Human Resources Services','4084','4017583','Ahmad','Rachid','Kaabour','','','1','11/05/1981','M','Lebanese','Married','Co-Nil','Payroll Services Manager I','ESSENTIAL PLUS - CLASS A','10','+96170115992','Ahmad.Kaabour@azadea.com','','','','','','','','','','','','','','xx','','','','','No','Employee','FULLT'],
      ['','','null','','12241','Jordan','7256','Azadea for Telecommunications and Technology L.L.C.','05CBEOFI','Financial Services','305774','4039564','Abdallah','Haitham','Alhayek','','','1','12/10/1999','M','Jordanian','Single','Co-Nil','Accounting Services Specialist','ESSENTIAL - CLASS SP','7','+962790319428','Abdallah.Alhayek@azadea.com','2000829483','','','','','','','','','','','','','xx','','','','','No','Employee','FULLT'],
    ];

    const csvContent = headers.join(',') + '\n' + sampleRows.map((row) => row.map((v) => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'list0_sample.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  function handleClear() {
    setEmployees([]);
    setFileName('');
    setResponse(null);
    setError(null);
  }

  return (
    <>
      <Toast ref={toastRef} />
      <ListZeroUi
        isLoading={submitMutation.isLoading}
        employees={employees}
        fileName={fileName}
        response={response}
        error={error}
        recentSubmissions={toSubmissionRows(recentSubmissions.data?.list)}
        totalSubmissions={recentSubmissions.data?.total || 0}
        isLoadingSubmissions={recentSubmissions.isLoading}
        first={pagination.first}
        rows={pagination.rows}
        handlers={{
          handleFileUpload,
          handleSubmit,
          handleClear,
          handleDownloadSample,
          handlePageChange,
        }}
      />
    </>
  );
}
